import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { enumHtsVerificationSource, enumRespErrors } from '@data-layer/enums/common';
import {
  HtsChatConversationModel,
  HtsChatConversationsModel,
  SimilarHtsConfirmModel,
  SimilarHtsSuggestionModel,
} from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { LineModel } from '@data-layer/models/entry-summaries';
import { HtsChatRepository } from '@data-layer/repositories';
import { HTS_ASSISTANT_ANSWER_MAX_LENGTH } from '../const/hts-assistant.const';
import { enumHtsAssistantMode } from '../enums';
import { HtsAssistantErrorHelper, IHtsAssistantErrorOutcome } from '../helpers/hts-assistant-error.helper';
import { HtsAssistantHelper } from '../helpers/hts-assistant.helper';
import {
  IHtsAssistantCurrentCodeTariff,
  IHtsAssistantHtsFound,
  IHtsAssistantInitConfig,
  IHtsAssistantState,
  IHtsAssistantViewModel,
} from '../interfaces';
import { HtsChatSessionService } from './hts-chat-session.service';
import { HtsCodeLookupService } from './hts-code-lookup.service';

/**
 * State of ONE classification assistant drawer (component-provided, disposed with it).
 * Talks to the backend through `HtsChatRepository`; all failures become inline notices, never global alerts.
 */
@Injectable()
export class HtsAssistantFacadeService implements OnDestroy {
  /** The line's code and tariff path a line-route response carries (#13550); `null` without a code (or a body). */
  private static tariffOf(response: HtsChatConversationsModel | null): IHtsAssistantCurrentCodeTariff | null {
    const digits: string = HtsAssistantHelper.toDigits(response && response.htsNumber);
    return digits ? { digits, levels: response.tariffDescription || [] } : null;
  }

  public readonly vm$: Observable<IHtsAssistantViewModel>;
  public readonly htsFound$: Subject<IHtsAssistantHtsFound> = new Subject<IHtsAssistantHtsFound>();
  public readonly lineUpdated$: Subject<LineModel> = new Subject<LineModel>();
  public readonly savedToCatalog$: Subject<{ conversationId: number; hts: string }> = new Subject<{
    conversationId: number;
    hts: string;
  }>();

  private state$: BehaviorSubject<IHtsAssistantState> = new BehaviorSubject<IHtsAssistantState>(HtsAssistantHelper.initialState());
  private isDestroy$: Subject<void> = new Subject<void>();
  /** Fires on every `init()`: a response for the previous line must never land in the new line's state. */
  private resetTurn$: Subject<void> = new Subject<void>();
  /** A newer suggestions request supersedes the one in flight. */
  private cancelSuggestions$: Subject<void> = new Subject<void>();
  private isDestroyed: boolean = false;

  constructor(
    private htsChatRepository: HtsChatRepository,
    private htsCodeLookupService: HtsCodeLookupService,
    private htsChatSessionService: HtsChatSessionService,
    private translateService: TranslateService,
  ) {
    this.vm$ = this.state$.pipe(map((state: IHtsAssistantState) => HtsAssistantHelper.buildViewModel(state)));
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isDestroy$.next();
    this.isDestroy$.complete();
  }

  // ---- lifecycle ---------------------------------------------------------------------------------

  public init(config: IHtsAssistantInitConfig): void {
    this.resetTurn$.next();
    // history hosts hand the conversations over (entry-level read): no per-line request
    const { conversations: preset, historyFinalConversationId, ...lineConfig }: IHtsAssistantInitConfig = config;
    const isPreset: boolean = Array.isArray(preset);
    this.state$.next({
      ...HtsAssistantHelper.initialState(),
      ...lineConfig,
      draftDescription: config.draftDescription === undefined ? null : config.draftDescription,
      isHostHeld: config.isHostHeld === true,
      isCatalogMode: config.isCatalogMode === true,
      verificationSource: config.verificationSource || enumHtsVerificationSource.None,
      verificationMatchedText: config.verificationMatchedText === undefined ? null : config.verificationMatchedText,
      verificationReasonText: config.verificationReasonText === undefined ? null : config.verificationReasonText,
      autoStartedIds: this.htsChatSessionService.getAutoStartedIds(),
      historyFinalConversationId: historyFinalConversationId === undefined ? null : historyFinalConversationId,
      openerTexts: {
        withDescription: this.translateService.instant('HTS_ASSISTANT.OPENER_WITH_DESCRIPTION'),
        catalog: this.translateService.instant('HTS_ASSISTANT.OPENER_CATALOG'),
      },
      isLoading: !isPreset && config.lineId !== null,
    });

    const empty: HtsChatConversationsModel = { lineId: config.lineId, htsNumber: null, tariffDescription: [], conversations: [] };
    const line$: Observable<HtsChatConversationsModel> = isPreset
      ? of({ ...empty, conversations: preset })
      : config.lineId !== null
      ? this.htsChatRepository.getLineConversations(config.lineId, config.scope).pipe(
          map((response: HtsChatConversationsModel) => response || empty),
          catchError((err: HttpErrorResponse) => {
            if (
              !HtsAssistantErrorHelper.isTag(err, enumRespErrors.HtsChatNotFound) &&
              !HtsAssistantErrorHelper.isTag(err, enumRespErrors.EntityIsNotFound)
            ) {
              this.showError(err);
            }
            return of(empty);
          }),
        )
      : of(empty);

    line$.pipe(takeUntil(this.isDestroy$), takeUntil(this.resetTurn$)).subscribe((response: HtsChatConversationsModel) => {
      // every conversation lives on the line (line routes): the active one is the one the backend marks active; the line's
      // code and its tariff path come along (#13550)
      this.patch({
        conversations: response.conversations || [],
        currentCodeTariff: HtsAssistantFacadeService.tariffOf(response),
        activeId: null,
        isLoading: false,
      });
      this.loadCatalog();
      // similar products first (#13517): with any on the table the chat waits for the user's decision
      this.loadSuggestions(() => this.maybeAutoStart(false));
    });
  }

  /** Hosts report the saved description on every reload; only a real change starts a conversation for the new text. */
  public setLineDescription(description: string | null): void {
    const previous: string = HtsAssistantHelper.normalizeDescription(this.state$.value.lineDescription);
    this.patch({ lineDescription: description });
    if (HtsAssistantHelper.normalizeDescription(description) !== previous) {
      // the backend ended the active conversation together with the description change: reflect it before deciding
      const active: HtsChatConversationModel | null = HtsAssistantHelper.findActive(this.state$.value);
      const conversations: HtsChatConversationModel[] =
        active && active.isActive
          ? HtsAssistantHelper.upsert(this.state$.value.conversations, { ...active, isActive: false, endedDate: new Date().toISOString() })
          : this.state$.value.conversations;
      // a new description: fresh suggestions, the earlier dismissal no longer counts; nothing is "active" any more
      this.patch({ conversations, activeId: null, isSuggestionsDismissed: false, isSuggestionsShown: false });
      this.loadSuggestions(() => this.maybeAutoStart(true));
    }
  }

  /**
   * The host is busy with the line (the Validate panel edits it, a backend insert is in flight): its description and
   * verification are about to change, so no conversation starts and no suggestions are fetched until it is released.
   */
  public setHostHold(isHeld: boolean): void {
    // the Validate panel releases its hold while the drawer is being torn down: nothing may start then
    if (this.isDestroyed || this.state$.value.isHostHeld === isHeld) {
      return;
    }
    this.patch({ isHostHeld: isHeld });
    if (!isHeld) {
      this.loadSuggestions(() => this.maybeAutoStart(false));
    }
  }

  // ---- similar products (#13517) ------------------------------------------------------------------------

  /** Dismiss: the chat starts, the list stays behind the toggle; while nothing can start (confirm in flight, host hold) it stays. */
  public dismissSuggestions(): void {
    if (this.isReadonly || !this.canStart(this.state$.value)) {
      return;
    }
    this.patch({ isSuggestionsDismissed: true, isSuggestionsShown: false, notice: null });
    this.startConversation();
  }

  public toggleSuggestions(): void {
    this.patch({ isSuggestionsShown: !this.state$.value.isSuggestionsShown });
  }

  /**
   * Insert of a suggestion: the backend writes only the code and verifies the line as "matched by similarity" (sibling
   * lines with the same supplier description too — hosts re-read the entry on `lineUpdated$`). No conversation starts.
   */
  public confirmSuggestion(suggestion: SimilarHtsSuggestionModel): void {
    const state: IHtsAssistantState = this.state$.value;
    if (this.isReadonly || state.lineId === null || state.isBusy || state.confirmingHts !== null || state.insertingId !== null) {
      return;
    }
    const body: SimilarHtsConfirmModel = {
      htsNumber: suggestion.htsNumber,
      conversationId: suggestion.conversationId,
      sourceLineId: suggestion.sourceLineId,
    };
    this.patch({ confirmingHts: suggestion.htsNumber, notice: null });
    this.htsChatRepository
      .confirmSuggestion(state.lineId, body)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (line: LineModel) => {
          const hts: string = HtsAssistantHelper.toDigits(line && line.htsNumber) || HtsAssistantHelper.toDigits(suggestion.htsNumber);
          this.patch({
            confirmingHts: null,
            isSuggestionsShown: false,
            currentHts: hts,
            // the line is verified now: the step shows the new code first (the host re-reads the entry and reports the rest)
            verificationSource:
              line && line.htsVerificationSource ? line.htsVerificationSource : enumHtsVerificationSource.SimilarityConfirmed,
            verificationMatchedText: line && line.htsVerificationMatchedText ? line.htsVerificationMatchedText : suggestion.matchedText,
          });
          this.htsFound$.next({ hts, description: null });
          this.lineUpdated$.next(line);
        },
        (err: HttpErrorResponse) => {
          this.patch({ confirmingHts: null });
          this.showError(err);
          // the list is out of date (description changed meanwhile, suggestion gone or withdrawn): ask again
          if (
            HtsAssistantErrorHelper.isTag(err, enumRespErrors.HtsSuggestionNotSimilar) ||
            HtsAssistantErrorHelper.isTag(err, enumRespErrors.HtsSuggestionNotFound) ||
            HtsAssistantErrorHelper.isTag(err, enumRespErrors.HtsSuggestionCodeWithdrawn)
          ) {
            this.loadSuggestions(() => undefined);
          }
        },
      );
  }

  /** Standalone host: the live form text; while it differs from the saved one the chat waits for Find HTS. */
  public setDraftDescription(description: string | null): void {
    this.patch({ draftDescription: description });
  }

  /** The line's verification as the host sees it after every reload (a verified line without a chat is not auto-started). */
  public setVerification(
    source: enumHtsVerificationSource | null | undefined,
    matchedText: string | null | undefined,
    reasonText: string | null = null,
  ): void {
    this.patch({
      verificationSource: source || enumHtsVerificationSource.None,
      verificationMatchedText: matchedText || null,
      verificationReasonText: reasonText,
    });
  }

  /** "Start a conversation" on the validated lock / Dismiss on the step: a chat on the user's explicit request. */
  public startConversation(): void {
    const state: IHtsAssistantState = this.state$.value;
    if (!this.canStart(state) || HtsAssistantHelper.findActive(state)) {
      return;
    }
    this.startForLine(state.lineId);
  }

  /**
   * The line's code as the host reports it is the source of truth: a code edited or cleared by hand (and saved) un-inserts
   * the cards whose result no longer matches, so Insert is offered again.
   */
  public setCurrentHts(hts: string | null): void {
    const state: IHtsAssistantState = this.state$.value;
    const digits: string = HtsAssistantHelper.toDigits(hts);
    const insertedIds: number[] = state.insertedIds.filter((id: number) => {
      const conversation: HtsChatConversationModel | undefined = state.conversations.find((c: HtsChatConversationModel) => c.id === id);
      const result: string = conversation && conversation.result ? HtsAssistantHelper.toDigits(conversation.result.htsNumber) : '';
      return !!digits && result === digits;
    });
    this.patch({ currentHts: hts, insertedIds });
  }

  public dismissNotice(): void {
    this.patch({ notice: null });
  }

  /** Do-over history (admin, Broker Review): reveal / hide the superseded conversations (#13385). */
  public toggleDoOver(): void {
    this.patch({ isShowDoOver: !this.state$.value.isShowDoOver });
  }

  // ---- conversation ------------------------------------------------------------------------------

  /** Emits once the backend accepted the answer (the composer clears on it); errors keep the text in the composer. */
  public send(text: string): Observable<void> {
    const state: IHtsAssistantState = this.state$.value;
    const trimmed: string = (text || '').trim();
    const active: HtsChatConversationModel | null = HtsAssistantHelper.findActive(state);
    const isAnswer: boolean = HtsAssistantHelper.isAwaitingAnswer(active);

    if (state.isBusy) {
      return this.reject('HTS_ASSISTANT.NOTICE.TURN_IN_PROGRESS', undefined, false);
    }
    if (!trimmed) {
      return this.reject('HTS_ASSISTANT.NOTICE.ANSWER_REQUIRED');
    }
    if (trimmed.length > HTS_ASSISTANT_ANSWER_MAX_LENGTH) {
      return this.reject('HTS_ASSISTANT.NOTICE.ANSWER_TOO_LONG', { max: HTS_ASSISTANT_ANSWER_MAX_LENGTH });
    }
    if (!isAnswer && !this.isCanStartByHand(state, active)) {
      return this.reject('HTS_ASSISTANT.NOTICE.OUR_SIDE');
    }

    // Optimistic: the bubble appears at once, the composer is cleared by the drawer, "Thinking" follows below.
    this.patch({ isBusy: true, notice: null, pendingUserMessage: trimmed });

    const request$: Observable<HtsChatConversationModel> = isAnswer
      ? this.htsChatRepository.answerConversation(active.id, trimmed)
      : this.startRequest(state, trimmed);

    return request$.pipe(
      take(1),
      takeUntil(this.resetTurn$),
      tap((conversation: HtsChatConversationModel) => this.acceptConversation(conversation)),
      map(() => undefined),
      catchError((err: HttpErrorResponse) => {
        this.patch({ isBusy: false, pendingUserMessage: null });
        // a local `reject` already set its notice; only a backend refusal is mapped
        if (err instanceof HttpErrorResponse) {
          this.showError(err);
        }
        return throwError(err);
      }),
    );
  }

  /**
   * "Start over" after a result or "no answer": the backend ends the current conversation and opens a new one from the
   * line's description. A conversation that already ended (another tab, a description edit) cannot be restarted
   * (`hts_chat_ended`): the idempotent line start opens the new one instead.
   * Catalog mode (#13554): the description is what the user typed, so `restarts` (a new conversation from the same first
   * message) would save the next code under the old description — the conversation is dropped locally instead, and the next
   * message opens a new one from a new description. The dropped conversation stays on the backend untouched: no line, no
   * catalog item, nothing reads it.
   */
  public restart(): void {
    const state: IHtsAssistantState = this.state$.value;
    const active: HtsChatConversationModel | null = HtsAssistantHelper.findActive(state);
    if (!active || this.isReadonly || state.isBusy) {
      return;
    }
    if (state.isCatalogMode) {
      this.patch(HtsAssistantHelper.clearedConversation());
      return;
    }
    if (!active.isActive) {
      if (state.lineId !== null) {
        this.startForLine(state.lineId);
      }
      return;
    }

    this.patch({ isBusy: true, notice: null });
    this.htsChatRepository
      .restartConversation(active.id)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (conversation: HtsChatConversationModel) => {
          // re-read the ended one: a save-to-catalog may have landed on it meanwhile
          const latest: HtsChatConversationModel =
            this.state$.value.conversations.find((c: HtsChatConversationModel) => c.id === active.id) || active;
          const ended: HtsChatConversationModel = { ...latest, isActive: false, endedDate: new Date().toISOString() };
          this.patch({ conversations: HtsAssistantHelper.upsert(this.state$.value.conversations, ended) });
          // the new conversation opens from the same stored description: hide its echo exactly like the first one's
          if (this.state$.value.autoStartedIds.includes(active.id)) {
            this.markAutoStarted([conversation.id]);
          }
          this.acceptConversation(conversation);
        },
        (err: HttpErrorResponse) => {
          this.patch({ isBusy: false });
          this.showError(err);
        },
      );
  }

  // ---- result actions ----------------------------------------------------------------------------

  public insert(conversationId: number): void {
    const state: IHtsAssistantState = this.state$.value;
    const conversation: HtsChatConversationModel = state.conversations.find((c: HtsChatConversationModel) => c.id === conversationId);
    const active: HtsChatConversationModel | null = HtsAssistantHelper.findActive(state);
    const isInsertable: boolean = !!active && active.id === conversationId && HtsAssistantHelper.isCompleted(conversation);
    if (this.isReadonly || !isInsertable || state.insertingId !== null || state.confirmingHts !== null || state.isBusy) {
      return;
    }
    const digits: string = HtsAssistantHelper.toDigits(conversation.result.htsNumber);
    // Only the code goes to the line: the description is the user's own and is never overwritten (#13384 F15).
    // Find HTS always saves the line before the drawer opens, so there is no "line does not exist yet" case any more.
    if (state.lineId === null) {
      return;
    }

    this.patch({ insertingId: conversationId, notice: null });
    // `applications` writes the code and verifies the line in one call; the conversation already lives on the line.
    // The clicked card's id goes along, so a stale click after a restart is refused instead of applying the wrong result.
    this.htsChatRepository
      .applyToLine(state.lineId, conversationId)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (line: LineModel) => {
          const hts: string = HtsAssistantHelper.toDigits(line && line.htsNumber) || digits;
          this.patch({
            insertedIds: [...this.state$.value.insertedIds, conversationId],
            insertingId: null,
            currentHts: hts,
            // the line is verified by the assistant now: nothing left to suggest
            suggestions: [],
            isSuggestionsDismissed: false,
            isSuggestionsShown: false,
          });
          this.htsFound$.next({ hts, description: null });
          this.lineUpdated$.next(line);
        },
        (err: HttpErrorResponse) => {
          this.patch({ insertingId: null });
          this.showError(err);
        },
      );
  }

  public saveToCatalog(conversationId: number): void {
    const state: IHtsAssistantState = this.state$.value;
    const conversation: HtsChatConversationModel = state.conversations.find((c: HtsChatConversationModel) => c.id === conversationId);
    if (this.isReadonly || !HtsAssistantHelper.isCompleted(conversation) || state.savingId !== null || state.companyId === null) {
      return;
    }
    const digits: string = HtsAssistantHelper.toDigits(conversation.result.htsNumber);

    this.patch({ savingId: conversationId, notice: null });
    // The conversation itself is saved (#13407): the backend attaches it to the company's item for the code or creates one,
    // so the item is marked "from the assistant" and validates a line about the same product. Idempotent on the backend.
    this.htsChatRepository
      .saveToCatalog(conversationId)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (item: FavoriteHtsNumberModel) => {
          // re-read the conversation: a restart may have ended it while the save was in flight
          const latest: HtsChatConversationModel =
            this.state$.value.conversations.find((c: HtsChatConversationModel) => c.id === conversationId) || conversation;
          const saved: HtsChatConversationModel = {
            ...latest,
            catalogItemId: item && item.id !== undefined ? item.id : latest.catalogItemId || null,
          };
          this.patch({
            conversations: HtsAssistantHelper.upsert(this.state$.value.conversations, saved),
            savedIds: [...this.state$.value.savedIds, conversationId],
            catalogHtsDigits: [...this.state$.value.catalogHtsDigits, digits],
            savingId: null,
          });
          this.savedToCatalog$.next({ conversationId, hts: digits });
        },
        (err: HttpErrorResponse) => {
          this.patch({ savingId: null });
          // a tagged refusal (ended / no answer / not finished / withdrawn code) explains itself; anything else is "save failed"
          if (HtsAssistantErrorHelper.hasTag(err)) {
            this.showError(err);
          } else {
            this.patch({ notice: { textKey: 'HTS_ASSISTANT.NOTICE.SAVE_FAILED', isError: true } });
          }
        },
      );
  }

  // ---- internals ---------------------------------------------------------------------------------

  private get isReadonly(): boolean {
    return this.state$.value.mode === enumHtsAssistantMode.Readonly;
  }

  /** Session + state: these conversations' first message is the line description put there by the client. */
  private markAutoStarted(ids: number[]): void {
    if (!ids.length) {
      return;
    }
    this.htsChatSessionService.markAutoStarted(ids);
    const current: number[] = this.state$.value.autoStartedIds;
    this.patch({ autoStartedIds: [...current, ...ids.filter((id: number) => !current.includes(id))] });
  }

  private acceptConversation(conversation: HtsChatConversationModel): void {
    this.patch({
      conversations: HtsAssistantHelper.upsert(this.state$.value.conversations, conversation),
      activeId: conversation.id,
      pendingUserMessage: null,
      isBusy: false,
    });
  }

  private refreshActive(): void {
    const active: HtsChatConversationModel | null = HtsAssistantHelper.findActive(this.state$.value);
    if (!active) {
      return;
    }
    this.htsChatRepository
      .getConversation(active.id)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (conversation: HtsChatConversationModel) => {
          this.patch({ conversations: HtsAssistantHelper.upsert(this.state$.value.conversations, conversation) });
        },
        // a failed refresh changes nothing: the notice that triggered it is already shown
        (): void => undefined,
      );
  }

  /**
   * The chat follows the SAVED description (#13384 F15). The line-route start is idempotent: it returns the active
   * conversation untouched and opens a new one only when there is none or the last one has ended — and a description edit
   * ends it on the backend. So after `init` it runs only without an active conversation; after a description change it
   * always runs (never `restarts`: that route is for Start over only).
   */
  private maybeAutoStart(isDescriptionChanged: boolean): void {
    const state: IHtsAssistantState = this.state$.value;
    if (!this.canStart(state)) {
      return;
    }
    // a verified line keeps its chat closed until the user asks for one (the "validated" lock offers the button)
    if (!isDescriptionChanged && HtsAssistantHelper.isVerifiedWithoutChat(state)) {
      return;
    }
    // similar products on the table: the user inserts one or dismisses the list — only then the chat starts
    if (HtsAssistantHelper.isSuggestionsPending(state)) {
      return;
    }
    if (isDescriptionChanged || !HtsAssistantHelper.findActive(state)) {
      this.startForLine(state.lineId);
    }
  }

  /**
   * Similar products for the line (live importer mode, saved description; a verified line gets them too, under its own
   * code). `done` runs once the list is known — also when nothing is fetched or the request fails: the chat must not wait.
   */
  private loadSuggestions(done: () => void): void {
    const state: IHtsAssistantState = this.state$.value;
    const isApplicable: boolean =
      HtsAssistantHelper.isLiveImporter(state) &&
      !state.isHostHeld &&
      state.lineId !== null &&
      !!HtsAssistantHelper.normalizeDescription(state.lineDescription);
    // whatever is in flight answers an older question: cancel it before deciding
    this.cancelSuggestions$.next();
    if (!isApplicable) {
      this.patch({ suggestions: [], isSuggestionsLoading: false });
      done();
      return;
    }
    this.patch({ isSuggestionsLoading: true });
    this.htsChatRepository
      .getLineSuggestions(state.lineId)
      .pipe(
        catchError(() => of([] as SimilarHtsSuggestionModel[])),
        take(1),
        takeUntil(this.cancelSuggestions$),
        takeUntil(this.isDestroy$),
        takeUntil(this.resetTurn$),
      )
      .subscribe((suggestions: SimilarHtsSuggestionModel[]) => {
        this.patch({ suggestions: suggestions || [], isSuggestionsLoading: false });
        done();
      });
  }

  /** Live importer mode on a saved line with a saved description, nothing in flight, the host not holding the line. */
  private canStart(state: IHtsAssistantState): boolean {
    return (
      HtsAssistantHelper.isLiveImporter(state) &&
      !state.isHostHeld &&
      !state.isLoading &&
      state.lineId !== null &&
      !state.isBusy &&
      state.insertingId === null &&
      state.confirmingHts === null &&
      !!HtsAssistantHelper.normalizeDescription(state.lineDescription)
    );
  }

  /** Line-route start: the response is the line's whole history (already linked), so no session entry is kept. */
  private startForLine(lineId: number): void {
    this.patch({ isBusy: true, notice: null });
    this.htsChatRepository
      .startForLine(lineId)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe(
        (response: HtsChatConversationsModel) => {
          this.acceptHistory(response);
          this.patch({ isBusy: false });
        },
        (err: HttpErrorResponse) => {
          this.patch({ isBusy: false });
          this.showError(err);
        },
      );
  }

  /**
   * A message with no question pending starts a conversation. On a line it needs the saved description (the line start
   * classifies it, the message answers). In catalog mode (#13554) the message itself is the description — but never while a
   * conversation is still active: the company start is not idempotent, unlike the line start.
   */
  private isCanStartByHand(state: IHtsAssistantState, active: HtsChatConversationModel | null): boolean {
    return state.isCatalogMode ? state.companyId !== null && !active : state.lineId !== null && !!state.lineDescription;
  }

  /** Catalog mode: the company start with the typed text as the description; otherwise the line start answered by it. */
  private startRequest(state: IHtsAssistantState, text: string): Observable<HtsChatConversationModel> {
    return state.isCatalogMode ? this.htsChatRepository.startForCompany(state.companyId, [text]) : this.startByHand(state.lineId, text);
  }

  /**
   * Start by hand after a failed auto-start: the line-route start classifies the SAVED description, then the typed text
   * answers the opening question. A start that already finished (Completed / NoAnswer on the first turn) lands as it is.
   */
  private startByHand(lineId: number, answer: string): Observable<HtsChatConversationModel> {
    return this.htsChatRepository.startForLine(lineId).pipe(
      map((response: HtsChatConversationsModel) => this.acceptHistory(response)),
      switchMap((active: HtsChatConversationModel | null) => {
        if (!active) {
          return this.reject('HTS_ASSISTANT.NOTICE.TRY_AGAIN');
        }
        if (HtsAssistantHelper.isAwaitingAnswer(active)) {
          return this.htsChatRepository.answerConversation(active.id, answer);
        }
        // the classifier finished on the first turn: the typed text was not needed (the drawer gives it back)
        return this.reject('HTS_ASSISTANT.NOTICE.ANSWER_NOT_NEEDED', undefined, false);
      }),
    );
  }

  /**
   * Merges a line-route response (the line's whole history, the line's code with its tariff path) into the state; returns
   * the active conversation, if any.
   */
  private acceptHistory(response: HtsChatConversationsModel): HtsChatConversationModel | null {
    const history: HtsChatConversationModel[] = (response && response.conversations) || [];
    const known: HtsChatConversationModel[] = this.state$.value.conversations;
    // only the conversation this call created (not a resumed one) carries the description echo as messages[0]
    const created: number[] = history
      .filter((c: HtsChatConversationModel) => !known.some((k: HtsChatConversationModel) => k.id === c.id))
      .map((c: HtsChatConversationModel) => c.id);
    this.markAutoStarted(created);
    const conversations: HtsChatConversationModel[] = history.reduce(
      (all: HtsChatConversationModel[], c: HtsChatConversationModel) => HtsAssistantHelper.upsert(all, c),
      known,
    );
    const active: HtsChatConversationModel | null = [...history].reverse().find((c: HtsChatConversationModel) => c.isActive) || null;
    this.patch({ conversations, currentCodeTariff: HtsAssistantFacadeService.tariffOf(response), activeId: active ? active.id : null });
    return active;
  }

  /** Codes already saved by the company: their cards show "Already in Bina Catalog" instead of the save action. */
  private loadCatalog(): void {
    const state: IHtsAssistantState = this.state$.value;
    if (!HtsAssistantHelper.isLiveImporter(state) || state.companyId === null) {
      return;
    }
    this.htsCodeLookupService
      .getActiveCatalogDigits(state.companyId)
      .pipe(take(1), takeUntil(this.isDestroy$), takeUntil(this.resetTurn$))
      .subscribe((digits: string[]) => this.patch({ catalogHtsDigits: digits }));
  }

  private reject(textKey: string, params?: { [key: string]: string | number }, isError: boolean = true): Observable<never> {
    this.patch({ notice: { textKey, params, isError } });
    return throwError(new Error(textKey));
  }

  /** Tag → inline notice (`HtsAssistantErrorHelper`); a lock switches the drawer to read-only, some refusals refresh the chat. */
  private showError(err: HttpErrorResponse): void {
    const outcome: IHtsAssistantErrorOutcome = HtsAssistantErrorHelper.map(err);
    this.patch({ notice: outcome.notice, ...(outcome.isLocked ? { mode: enumHtsAssistantMode.Readonly } : {}) });
    if (outcome.isRefreshActive) {
      this.refreshActive();
    }
  }

  private patch(partial: Partial<IHtsAssistantState>): void {
    this.state$.next({ ...this.state$.value, ...partial });
  }
}
